import InvoiceMetadataEditor from "../components/InvoiceMetadataEditor";
import DocumentDateEditor from "../components/DocumentDateEditor";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError, API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import {
  FilePdf, FileXls, PencilSimple, FloppyDisk, XCircle,
  Copy, Check, X, ArrowLeft,
} from "@phosphor-icons/react";
import { useI18n } from "@/lib/i18n";

const CS = { USD: "$", GBP: "£", EUR: "€", TRY: "₺" };

export default function InvoiceDetail() {
  const { t } = useI18n();
  const { id } = useParams();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);
  const [settings, setSettings] = useState({});
  const [editingNo, setEditingNo] = useState(false);
  const [tempNo, setTempNo] = useState("");

  const load = async () => {
    const r = await api.get(`/invoices/${id}`);
    setInv(r.data);
  };

  useEffect(() => {
    load();
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [id]);

  if (!inv) return <div className="p-8 text-slate-500">{t("invoice_detail.loading")}</div>;
  const sym = CS[inv.currency] || inv.currency;
  const isCancelled = inv.status === "cancelled";

  const download = async (fmt) => {
    try {
      const token = localStorage.getItem("pallet_token");
      const res = await fetch(`${API}/invoices/${id}/${fmt}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(t("final.download_failed"));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `invoice_${inv.invoice_no}.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const saveInvoiceNo = async () => {
    if (!tempNo.trim()) { setEditingNo(false); return; }
    try {
      await api.put(`/invoices/${id}`, { invoice_no: tempNo.trim() });
      toast.success("Fatura no güncellendi");
      setEditingNo(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const cancel = async () => {
    if (!window.confirm("Bu fatura iptal edilsin mi? (Silinmez, üzerine iptal işareti konur)")) return;
    try {
      await api.post(`/invoices/${id}/cancel`);
      toast.success("Fatura iptal edildi");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const duplicate = async () => {
    try {
      const r = await api.post(`/invoices/${id}/duplicate`);
      toast.success(`Kopya oluşturuldu: ${r.data.invoice_no}`);
      nav(`/invoices/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <span>Invoice</span>
            {editingNo ? (
              <span className="flex items-center gap-1">
                <input
                  data-testid="edit-invoice-no-input"
                  autoFocus value={tempNo}
                  onChange={(e) => setTempNo(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveInvoiceNo(); if (e.key === "Escape") setEditingNo(false); }}
                  className="font-mono text-xl font-bold border-b-2 border-slate-900 outline-none bg-transparent px-1 w-64" />
                <button onClick={saveInvoiceNo} data-testid="save-invoice-no"
                  className="text-slate-900 hover:bg-slate-100 p-1"><Check size={18} weight="bold" /></button>
                <button onClick={() => setEditingNo(false)}
                  className="text-slate-500 hover:bg-slate-100 p-1"><X size={18} /></button>
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <span className="font-mono">{inv.invoice_no}</span>
                {!isCancelled && (
                  <button data-testid="edit-invoice-no" onClick={() => { setTempNo(inv.invoice_no); setEditingNo(true); }}
                    className="text-slate-400 hover:text-slate-900" title={t("final.invoice.edit_no")}>
                    <PencilSimple size={16} />
                  </button>
                )}
                {isCancelled && (
                  <span className="text-xs uppercase font-semibold bg-slate-900 text-white px-2 py-0.5 border border-slate-900" data-testid="cancelled-badge">
                    İPTAL / CANCELLED
                  </span>
                )}
              </span>
            )}
          </span>
        }
        subtitle={`Plan ${inv.plan_no} · ${inv.consignee || inv.shipment_name} · ${inv.date}`}>
        <button onClick={() => nav("/invoices")} data-testid="btn-back-invoices"
          className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-900 border border-slate-300 px-3 py-2 text-sm font-semibold rounded-sm">
          <ArrowLeft size={16} weight="bold" /> Commercial Invoice'a Dön
        </button>
        {!isCancelled && (
          <button onClick={duplicate} data-testid="btn-duplicate"
            className="inline-flex items-center gap-2 bg-slate-200 hover:bg-slate-300 text-slate-900 px-3 py-2 text-sm font-semibold rounded-sm">
            <Copy size={16} weight="bold" /> Kopyala
          </button>
        )}
        {!isCancelled && (
          <button onClick={cancel} data-testid="btn-cancel-invoice"
            className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-slate-900 border border-slate-900 px-3 py-2 text-sm font-semibold rounded-sm">
            <XCircle size={16} weight="bold" /> {t("final.invoice.cancel")}
          </button>
        )}
        <button onClick={() => download("pdf")} data-testid="btn-inv-pdf"
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-3 py-2 text-sm font-semibold rounded-sm">
          <FilePdf size={16} weight="bold" /> PDF
        </button>
        <button onClick={() => download("xlsx")} data-testid="btn-inv-xlsx"
          className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-800 text-white px-3 py-2 text-sm font-semibold rounded-sm">
          <FileXls size={16} weight="bold" /> Excel
        </button>
      </PageHeader>
      <InvoiceMetadataEditor invoice={inv} onSaved={load} />
      <DocumentDateEditor date={inv.date} path={`/invoices/${id}`} label="Fatura Tarihi" disabled={isCancelled} onSaved={load} />

      <div className="p-4 sm:p-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {isCancelled && (
          <div className="lg:col-span-3 bg-slate-100 border-l-4 border-slate-900 text-slate-900 text-sm px-4 py-3">
            <b>{t("final.invoice.cancelled_info")}</b> Yeni bir belge için "Kopyala" seçeneği kullanılabilir.
          </div>
        )}
        {inv.has_currency_mismatch && !inv.converted_prices && (
          <div className="lg:col-span-3 bg-slate-50 border border-slate-300 text-slate-800 text-sm px-4 py-3">
            <b>{t("final.invoice.mixed_currency")}:</b> Bazı ürünler farklı para biriminde tanımlı ve döviz çevirisi yapılmadı.
          </div>
        )}
        {inv.converted_prices && inv.exchange_rates && (
          <div className="lg:col-span-3 bg-slate-50 border border-slate-300 text-slate-700 text-xs px-4 py-2 font-mono">
            Fiyatlar {inv.exchange_rates.source} kuruyla ({inv.exchange_rates.date}) {inv.currency}'e çevrildi.
          </div>
        )}
        <div className="lg:col-span-2 bg-white border border-slate-200 overflow-x-auto">
          <table className="data-table w-full min-w-[720px]">
            <thead><tr>
              <th>#</th><th>SKU</th><th>{t("final.invoice.description")}</th>
              <th className="text-right">Adet</th>
              <th className="text-right">Birim Fiyat</th>
              <th className="text-right">{t("invoice.total")}</th>
              <th className="text-right">Net kg</th>
            </tr></thead>
            <tbody>
              {inv.items.map((it, i) => (
                <tr key={it.sku}>
                  <td className="text-center">{i + 1}</td>
                  <td className="font-mono text-xs font-bold">
                    {it.sku}
                    {it.converted && (
                      <span className="ml-2 text-[9px] font-semibold text-slate-900 bg-slate-100 border border-slate-900 px-1 py-0.5 uppercase"
                        title={`Orijinal: ${CS[it.product_currency]}${it.original_unit_price} ${it.product_currency}`}>
                        çevrildi
                      </span>
                    )}
                    {it.currency_mismatch && (
                      <span className="ml-2 text-[9px] font-semibold text-slate-700 bg-slate-200 border border-slate-400 px-1 py-0.5 uppercase">
                        {it.product_currency}
                      </span>
                    )}
                  </td>
                  <td>{it.description}</td>
                  <td className="text-right data-cell">{it.quantity}</td>
                  <td className="text-right data-cell font-mono">
                    {sym}{Number(it.unit_price).toFixed(2)}
                  </td>
                  <td className="text-right data-cell font-mono font-bold">{sym}{Number(it.line_total).toFixed(2)}</td>
                  <td className="text-right data-cell">{Number(it.total_product_kg ?? (Number(it.unit_product_kg || 0) * Number(it.quantity || 0))).toFixed(2)}</td>
                </tr>
              ))}
              <tr className="bg-slate-900 text-white">
                <td colSpan={3} className="font-bold">GRAND TOTAL</td>
                <td className="text-right data-cell font-bold">{inv.total_qty}</td>
                <td></td>
                <td className="text-right data-cell font-bold font-mono">{sym}{Number(inv.subtotal).toFixed(2)}</td>
                <td className="text-right data-cell font-bold">{Number(inv.total_net_weight ?? inv.total_kg).toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="space-y-3">
          <div className="bg-white border border-slate-200 p-5">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{t("invoice_detail.shipment_info")}</div>
            <div className="mt-2 space-y-1.5 text-sm">
              <Kv l="INCOTERMS" v={inv.incoterms} />
              <Kv l={t("invoice_detail.shipping")} v={inv.shipping_method || "—"} />
              <Kv l="Carrier" v={inv.carrier || "—"} />
              <Kv l="Seller" v={inv.seller_exporter || "—"} />
              <Kv l="Currency" v={inv.currency} />
              <Kv l={t("invoice_detail.pallet")} v={inv.total_pallets} />
              <Kv l="Ürün Net" v={`${Number(inv.total_net_weight ?? inv.total_kg).toFixed(2)} kg`} />
              <Kv l="Ambalaj" v={`${Number(inv._weight_breakdown?.packaging_kg || 0).toFixed(2)} kg`} />
              <Kv l="Paletler" v={`${Number(inv._weight_breakdown?.pallet_tare_kg || 0).toFixed(2)} kg`} />
              <Kv l="Sevkiyat Brüt" v={`${Number(inv.total_gross_weight ?? inv.total_kg).toFixed(2)} kg`} />
            </div>
          </div>
          <div className="bg-slate-900 text-white p-5">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">{t("invoice_detail.total_amount")}</div>
            <div className="font-display text-4xl font-extrabold mt-1">
              {sym}{Number(inv.subtotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="text-xs text-slate-400 mt-1">{inv.currency}</div>
          </div>
          {inv.note && (
            <div className="bg-white border border-slate-200 p-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Not</div>
              <div className="mt-1 text-sm italic text-slate-700">{inv.note}</div>
            </div>
          )}
          {(settings.bank_name || settings.bank_iban || settings.bank_swift) && (
            <div className="bg-white border border-slate-200 p-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Banka Bilgileri</div>
              <div className="text-xs space-y-1">
                {settings.bank_name && <div><span className="text-slate-500">Bank:</span> <b>{settings.bank_name}</b></div>}
                {settings.bank_account_holder && <div><span className="text-slate-500">{t("invoice_detail.account_holder")}:</span> <b>{settings.bank_account_holder}</b></div>}
                {settings.bank_iban && <div><span className="text-slate-500">IBAN:</span> <b className="font-mono">{settings.bank_iban}</b></div>}
                {settings.bank_swift && <div><span className="text-slate-500">SWIFT:</span> <b className="font-mono">{settings.bank_swift}</b></div>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Kv({ l, v }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-500">{l}</span>
      <span className="font-mono font-semibold">{v}</span>
    </div>
  );
}

